/**
 * SyncHealthPanel (Phase 2, §7.5) — the recent-sync-runs log.
 *
 * Each run shows its bank label (joined from `useConnections`), a trigger badge
 * (Geplant/Manuell/Erstimport), a status badge, timestamps, and an expandable
 * per-account breakdown projected from `sync_runs.results`. Optionally scoped to
 * one connection.
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { SYNC_ACCOUNT_STATUS, SYNC_RUN_STATUS, SYNC_TRIGGER } from '../../lib/labels.ts';
import { useConnections } from '../../lib/hooks/useConnections.ts';
import { useSyncRuns } from '../../lib/hooks/useSyncRuns.ts';
import type { SyncAccountResult, SyncRunOut, SyncRunStatus, SyncTrigger } from '../../lib/types.ts';

// Solid pastel pills per DESIGN.md §3.11.
const RUN_STATUS_STYLE: Record<SyncRunStatus, string> = {
  running: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  success: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  partial: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  error: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
};

const TRIGGER_STYLE: Record<SyncTrigger, string> = {
  scheduled: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  manual: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  bootstrap: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
};

function Badge({ className, children }: { className: string; children: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}
    >
      {children}
    </span>
  );
}

export interface SyncHealthPanelProps {
  connectionId?: number;
  limit?: number;
}

export function SyncHealthPanel({ connectionId, limit = 20 }: SyncHealthPanelProps) {
  const runsQuery = useSyncRuns({ limit, connection_id: connectionId });
  const connectionsQuery = useConnections();

  const labelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const conn of connectionsQuery.data ?? []) map.set(conn.id, conn.bank_label);
    return map;
  }, [connectionsQuery.data]);

  if (runsQuery.isPending) return <SkeletonList rows={3} />;
  if (runsQuery.isError) {
    return <ErrorState error={runsQuery.error} onRetry={() => void runsQuery.refetch()} />;
  }
  const runs = runsQuery.data;
  if (runs.length === 0) {
    return (
      <EmptyState
        title="Noch keine Abgleiche"
        description="Sobald ein automatischer oder manueller Abgleich läuft, erscheint er hier."
      />
    );
  }

  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
      {runs.map((run) => (
        <SyncRunRow key={run.id} run={run} bankLabel={labelById.get(run.connection_id)} />
      ))}
    </ul>
  );
}

function SyncRunRow({ run, bankLabel }: { run: SyncRunOut; bankLabel: string | undefined }) {
  const [open, setOpen] = useState(false);
  const hasAccounts = run.accounts.length > 0;

  return (
    <li>
      <button
        type="button"
        onClick={() => hasAccounts && setOpen((v) => !v)}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded px-2 py-3 text-left transition-colors duration-150 hover:bg-slate-50 dark:hover:bg-slate-800/60"
        aria-expanded={hasAccounts ? open : undefined}
      >
        {hasAccounts ? (
          open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
          )
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
              {bankLabel ?? `Verbindung #${run.connection_id}`}
            </span>
            <Badge className={TRIGGER_STYLE[run.trigger]}>{SYNC_TRIGGER[run.trigger]}</Badge>
            <Badge className={RUN_STATUS_STYLE[run.status]}>{SYNC_RUN_STATUS[run.status]}</Badge>
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-400 dark:text-slate-500">
            {formatDateTime(run.started_at)}
            {run.finished_at ? ` – ${formatDateTime(run.finished_at)}` : ' · läuft …'}
          </p>
          {run.error ? (
            <p className="mt-0.5 truncate text-xs text-rose-600 dark:text-rose-400">{run.error}</p>
          ) : null}
        </div>
      </button>

      {open && hasAccounts ? (
        <ul className="space-y-1 border-t border-slate-100 py-2 pl-6 dark:border-slate-800/70">
          {run.accounts.map((acc) => (
            <AccountResultRow key={acc.account_id} result={acc} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function AccountResultRow({ result }: { result: SyncAccountResult }) {
  return (
    <li className="text-xs text-slate-600 dark:text-slate-400">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium tabular-nums">Konto #{result.account_id}</span>
        <span className="tabular-nums text-slate-400 dark:text-slate-500">
          {SYNC_ACCOUNT_STATUS[result.status]}
          {' · '}
          {result.imported} neu · {result.updated} aktualisiert · {result.pending_replaced}{' '}
          vorgemerkt · {result.skipped_duplicate} Duplikate · {result.balances} Salden
        </span>
      </div>
      {result.error ? (
        <p className="mt-0.5 text-rose-600 dark:text-rose-400">{result.error}</p>
      ) : null}
    </li>
  );
}
